import { useMemo } from 'react';

import { useAtomValue } from 'jotai';

import { useCategories, useLayoutCustomizations, useUiParameters } from '@queries';
import { activityAtom } from '@store';

const POSITIONS = [8, 7, 6, 4, 2, 1];

export const useSwingFoundationParameters = (activePosition: number) => {
    const activeSwingFoundationPosition = useMemo(
        // filter by enabled swing foundation positions
        () => POSITIONS.find((pos) => pos <= activePosition),
        [activePosition],
    );

    const { data: parameters } = useUiParameters();
    const paramMap = useMemo(
        () => Object.fromEntries(parameters?.map((param) => [param.id, param] as const) || []),
        [parameters],
    );
    const { data: swingFoundationGroups } = useCategories('swing_foundation_group');
    const groupMap = useMemo(
        () => Object.fromEntries(swingFoundationGroups?.map((group) => [group.id, group] as const) || []),
        [swingFoundationGroups],
    );
    const activity = useAtomValue(activityAtom);
    const { data: customizations } = useLayoutCustomizations(activity?.layoutId || -1);
    const swingFoundationParameterIds = useMemo(() => {
        const allParameters = customizations
            ?.filter(
                (customization) =>
                    customization.parentUiNodeId?.includes('swing_foundations.default_kiosk') &&
                    customization.showChildOn?.includes('kiosk'),
            )
            .map((customization) => customization.childUiNodeId);

        return allParameters?.sort((paramId, nextParamId): number => {
            const paramGroup = groupMap[paramMap?.[paramId]?.categories.swingFoundationGroup || ''];
            const nextParamGroup = groupMap[paramMap?.[nextParamId]?.categories.swingFoundationGroup || ''];
            return paramGroup?.displayOrder - nextParamGroup?.displayOrder;
        });
    }, [customizations, groupMap, paramMap]);

    const swingFoundationParameters = useMemo(() => {
        return parameters?.filter(
            ({ id, categories }) =>
                swingFoundationParameterIds?.includes(id) &&
                categories.swingFoundationGroup?.includes('p' + activeSwingFoundationPosition),
        );
    }, [activeSwingFoundationPosition, parameters, swingFoundationParameterIds]);

    return swingFoundationParameters;
};
